import {
  DocumentIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { IndexSourceDocumentsQuery, SourceType } from '@sc/graphql';
import { getFileSizeInMb, IconButton, SourceIcon } from '@sc/shared-ui';
import { ReactElement, ReactNode } from 'react';
import { useAlerts } from '../providers/alerts';
import { useMutation } from '@apollo/client/react';
import {
  GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_QUERY_INDEX_SOURCES,
} from '../utility/queries';
import { notify } from '../utility/notify';
import { failedReasonLabel } from '../utility/failedReason';

type SD = IndexSourceDocumentsQuery['indexSourceDocuments']['items'][number];

interface Props {
  sd: SD;
  sourceUuid: string;
  sourceType: SourceType;
  getPath: (sd: SD) => ReactNode;
  FallbackIcon: HeroIconType;
}

export const SourceDocumentCard = ({
  sd,
  sourceUuid,
  FallbackIcon,
  sourceType,
  getPath,
}: Props): ReactElement => {
  const { setAlertModal, useApolloError } = useAlerts();
  const onError = useApolloError();

  const [deleteSourceDocument] = useMutation(
    GQL_MUTATION_UPDATE_SOURCE_DOCUMENTS,
    {
      onError,
      onCompleted: () => {
        notify.success('Successfully deleted source document');

        setAlertModal(undefined);
      },
      optimisticResponse: {
        updateSourceDocuments: true,
      },
      update: (cache, data, context) =>
        cache.updateQuery(
          {
            query: GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
            variables: {
              dimensionsInput: undefined,
              input: {
                filter: {
                  sourceUuid,
                },
                pagination: {
                  perPage: 20,
                  page: 1,
                },
              },
            },
          },
          (d) =>
            d
              ? {
                  ...d,
                  indexSourceDocuments: {
                    ...d.indexSourceDocuments,
                    items: d.indexSourceDocuments.items.filter(
                      (i) =>
                        !context.variables?.input.documentsToDelete.includes(
                          i.uuid
                        )
                    ),
                  },
                }
              : null
        ),
    }
  );

  const path = getPath(sd);

  return sourceType === SourceType.file ? (
    <div className="flex items-center space-x-2 text-body-smallest border border-grey-700 rounded-xs pl-2">
      <DocumentIcon className="size-5 text-grey-400" />
      <div className="grow space-x-2">
        <span>{sd.title}</span>
        <span>•</span>
        <span>
          {getFileSizeInMb(sd.description ? parseInt(sd.description) : 0)}
        </span>
      </div>
      <IconButton
        type="delete"
        Icon={TrashIcon}
        onClick={() =>
          setAlertModal({
            open: true,
            title: `Delete file`,
            description:
              `Are you sure you want to delete ${sd.title}? It will be removed from your knowledge base.`,
            icon: 'critical',
            isDelete: true,
            submitText: 'Confirm deletion',
            onSubmit: () => {
              setAlertModal(undefined);

              void deleteSourceDocument({
                variables: {
                  input: {
                    uuid: sourceUuid,
                    documentsToDelete: [sd.uuid],
                    documentsToCreate: [],
                    integrationType: undefined
                  },
                },
                refetchQueries: [
                  GQL_QUERY_INDEX_SOURCES,
                  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
                  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
                ],
              });
            },
          })
        }
      />
    </div>
  ) : sourceType === SourceType.notion ? (
    <div className="flex items-center space-x-2 text-body-smallest border border-grey-700 rounded-xs px-2 py-1.5">
      <SourceIcon type={sourceType} className="size-5 shrink-0" />
      <div className="grow flex items-center space-x-2 min-w-0">
        <span className="truncate">{sd.title}</span>
        {sd.failedReason ? (
          <span className="shrink-0 flex items-center gap-x-1 text-red-400">
            <XCircleIcon className="size-3.5" />
            {failedReasonLabel(sd.failedReason)}
          </span>
        ) : (
          !!path && (
            <>
              <span className="shrink-0 text-text-subtle">•</span>
              <span className="truncate text-text-subtle">{path}</span>
            </>
          )
        )}
      </div>
      <IconButton
        type="delete"
        Icon={TrashIcon}
        onClick={() =>
          setAlertModal({
            open: true,
            title: `Delete ${sd.title}`,
            description:
              'Are you sure you want to delete this document? It will be removed from your knowledge base.',
            icon: 'critical',
            isDelete: true,
            submitText: 'Confirm deletion',
            onSubmit: () => {
              setAlertModal(undefined);

              void deleteSourceDocument({
                variables: {
                  input: {
                    uuid: sourceUuid,
                    documentsToDelete: [sd.uuid],
                    documentsToCreate: [],
                    integrationType: undefined,
                  },
                },
                refetchQueries: [
                  GQL_QUERY_INDEX_SOURCES,
                  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
                  GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
                ],
              });
            },
          })
        }
      />
    </div>
  ) : (
    <div className="border flex rounded-xs border-grey-t-800 max-h-45">
      {sd.thumbnail?.src ? (
        <img alt={`${sd.title} thumbnail`} src={sd.thumbnail.src} />
      ) : (
        <div className="flex size-45 shrink-0 items-center justify-center bg-grey-t-900 p-1">
          <FallbackIcon className="size-11" />
        </div>
      )}
      <div className="p-3 space-y-2.5 grow overflow-hidden">
        <div className="flex items-center gap-x-2 overflow-hidden">
          <SourceIcon type={sourceType} className="size-4 shrink-0" />
          {path}
        </div>
        {sd.failedReason && (
          <div className="flex items-center gap-x-1 text-red-400 text-body-smallest">
            <XCircleIcon className="size-4 shrink-0" />
            <span>{failedReasonLabel(sd.failedReason)}</span>
          </div>
        )}
        <h4 className="text-h4 font-grotesque font-semibold truncate">
          {sd.title}
        </h4>
        <p className="text-sm text-text-subtle line-clamp-2">
          {sd.description}
        </p>
        <div className="flex justify-end mt-3">
          <IconButton
            type="delete"
            Icon={TrashIcon}
            onClick={() =>
              setAlertModal({
                open: true,
                title: `Delete ${sd.title}`,
                description:
                  'Are you sure you want to delete this document? It will be removed from your knowledge base.',
                icon: 'critical',
                isDelete: true,
                submitText: 'Confirm deletion',
                onSubmit: () => {
                  void deleteSourceDocument({
                    variables: {
                      input: {
                        uuid: sourceUuid,
                        documentsToDelete: [sd.uuid],
                        documentsToCreate: [],
                        integrationType: undefined
                      },
                    },
                    refetchQueries: [
                      GQL_QUERY_INDEX_SOURCES,
                      GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
                      GQL_QUERY_INDEX_SOURCE_DOCUMENT_ID,
                    ],
                  });
                },
              })
            }
          />
        </div>
      </div>
    </div>
  );
};
